/**
 * Art Style definitions for children's book illustrations.
 *
 * This file contains both FE-safe data (title, description, emoji) and BE-only data (prompts).
 * - FE should import: ART_STYLE_OPTIONS, ART_STYLE_DISPLAY
 * - BE should import: ART_STYLES (includes prompts)
 */

/**
 * Art style keys - used for type safety and as enum values.
 */
export const ART_STYLE_KEYS = [
  'soft_watercolor',
  'modern_3d_cartoon',
  'whimsical_cartoon',
  'vintage_storybook',
  'paper_cut_collage',
  'digital_painterly',
  'cute_kawaii',
  'line_art_wash',
  'flat_vector_modern',
] as const;

export type ArtStyleKey = (typeof ART_STYLE_KEYS)[number];

/**
 * Full art style definition including prompts (BE use only).
 */
export interface ArtStyleDefinition {
  /** Display title for the style */
  title: string;
  /** User-friendly description */
  description: string;
  /** Emoji icon for UI display */
  emoji: string;
  /** AI prompt fragment for image generation (BE only) */
  stylePrompt: string;
  /** Suggested themes this style works best with */
  bestFor: string[];
}

/**
 * FE-safe display data (no prompts).
 */
export interface ArtStyleDisplay {
  title: string;
  description: string;
  emoji: string;
}

/**
 * Complete art style definitions with prompts.
 * Use this on the backend for image generation.
 */
export const ART_STYLES: Record<ArtStyleKey, ArtStyleDefinition> = {
  soft_watercolor: {
    title: 'Soft Watercolor',
    description:
      'Dreamy, delicate illustrations with gentle brushstrokes, transparent color washes, and organic textures. Evokes warmth, nostalgia, and fairy-tale magic. Perfect for bedtime stories and gentle narratives.',
    emoji: '🎨',
    stylePrompt:
      "soft watercolor illustration style, delicate brushstrokes, transparent color washes, gentle gradients, dreamy atmosphere, organic textures, hand-painted aesthetic, classic children's book illustration, warm and nostalgic feeling, subtle color bleeding,",
    bestFor: [
      'bedtime stories',
      'nature themes',
      'fairy tales',
      'emotional narratives',
    ],
  },

  modern_3d_cartoon: {
    title: 'Modern 3D Cartoon',
    description:
      'Polished, dimensional characters with expressive features, smooth textures, and cinematic lighting. Professional animation quality with vibrant colors. Appeals to contemporary audiences.',
    emoji: '🎬',
    stylePrompt:
      "3D rendered cartoon illustration, smooth textures, expressive character design, big eyes, rounded shapes, cinematic lighting, vibrant colors, professional animation quality, soft shadows, depth of field, modern children's animation style,",
    bestFor: [
      'adventure stories',
      'action scenes',
      'contemporary settings',
      'expressive characters',
    ],
  },

  whimsical_cartoon: {
    title: 'Whimsical Cartoon',
    description:
      'Playful, exaggerated characters with bold outlines, simplified shapes, and vibrant flat colors. Fun and energetic with a sense of humor. Highly engaging for young children ages 2-6.',
    emoji: '🎪',
    stylePrompt:
      "whimsical cartoon illustration, bold black outlines, flat vibrant colors, exaggerated expressions, simplified shapes, playful characters, fun energetic style, rounded friendly forms, cheerful atmosphere, children's picture book cartoon,",
    bestFor: [
      'humorous stories',
      'silly adventures',
      'learning books',
      'younger children',
    ],
  },

  vintage_storybook: {
    title: 'Vintage Storybook',
    description:
      'Classic, timeless illustrations with a nostalgic golden-age feel. Features warm earth tones, detailed linework, and traditional composition. Reminiscent of classic fairy tale collections.',
    emoji: '📖',
    stylePrompt:
      "vintage storybook illustration style, classic golden age aesthetic, warm earth tones, detailed linework, traditional composition, nostalgic feeling, retro children's book art, mid-century illustration, textured paper appearance, timeless fairy tale quality,",
    bestFor: [
      'classic fairy tales',
      'heritage stories',
      'grandparent gifts',
      'nostalgic themes',
    ],
  },

  paper_cut_collage: {
    title: 'Paper Cut Collage',
    description:
      'Layered paper textures creating depth and dimension. Features cut paper shapes, tissue paper overlays, and mixed media elements. Tactile and artistic with a handcrafted appearance.',
    emoji: '✂️',
    stylePrompt:
      'paper cut collage illustration style, layered paper textures, cut paper shapes, tissue paper overlay effect, mixed media aesthetic, tactile dimensional look, visible paper grain, handcrafted appearance, colorful torn paper edges, textured backgrounds,',
    bestFor: [
      'nature stories',
      'counting books',
      'educational content',
      'artistic narratives',
    ],
  },

  digital_painterly: {
    title: 'Digital Painterly',
    description:
      'Rich, detailed illustrations combining digital precision with painterly textures. Lush environments, atmospheric lighting, and cinematic compositions. Sophisticated yet accessible.',
    emoji: '🖌️',
    stylePrompt:
      "digital painting illustration, rich detailed textures, painterly brushwork, atmospheric lighting, lush environments, detailed backgrounds, cinematic composition, professional book illustration, vibrant saturated colors, visible brush strokes, studio quality children's art,",
    bestFor: [
      'fantasy adventures',
      'epic journeys',
      'detailed world-building',
      'older children',
    ],
  },

  cute_kawaii: {
    title: 'Cute Kawaii',
    description:
      'Adorable, simplified characters with rounded features, big sparkly eyes, and soft pastel colors. Japanese-inspired aesthetic that emphasizes cuteness and charm. Universal appeal across cultures.',
    emoji: '🌸',
    stylePrompt:
      'kawaii cute illustration style, adorable rounded characters, big sparkly eyes, soft pastel colors, chibi proportions, sweet expressions, Japanese cute aesthetic, minimalist backgrounds, blushing cheeks, simple shapes, cheerful atmosphere,',
    bestFor: [
      'character-focused stories',
      'pet stories',
      'friendship themes',
      'gift books',
    ],
  },

  line_art_wash: {
    title: 'Line Art with Color Wash',
    description:
      'Clean ink drawings with loose watercolor or digital color fills. Emphasizes expressive linework while maintaining a light, airy feeling. Elegant balance between detail and simplicity.',
    emoji: '✒️',
    stylePrompt:
      'line art illustration with watercolor wash, clean ink drawing, expressive linework, loose color fills, hand-drawn aesthetic, sketch-like quality, airy light feeling, pen and watercolor style, minimal backgrounds, elegant simplicity,',
    bestFor: [
      'chapter book illustrations',
      'poetry books',
      'activity narratives',
      'older picture books',
    ],
  },

  flat_vector_modern: {
    title: 'Flat Vector Modern',
    description:
      'Clean geometric shapes, bold solid colors, and minimal gradients. Contemporary graphic design aesthetic with strong visual impact. Highly readable and scalable for various formats.',
    emoji: '📐',
    stylePrompt:
      "flat vector illustration style, clean geometric shapes, bold solid colors, minimal gradients, modern graphic design, sharp edges, contemporary aesthetic, strong silhouettes, limited color palette, minimalist composition, trendy children's book art,",
    bestFor: [
      'concept books',
      'educational content',
      'modern themes',
      'infographic-style stories',
    ],
  },
};

/**
 * Art style options array for schema validation.
 * Use this in Zod schemas and for iteration.
 */
export const ART_STYLE_OPTIONS = ART_STYLE_KEYS;

/**
 * FE-safe display data without prompts.
 * Use this on the frontend for showing options to users.
 */
export const ART_STYLE_DISPLAY: Record<ArtStyleKey, ArtStyleDisplay> =
  Object.fromEntries(
    ART_STYLE_KEYS.map((key) => [
      key,
      {
        title: ART_STYLES[key].title,
        description: ART_STYLES[key].description,
        emoji: ART_STYLES[key].emoji,
      },
    ])
  ) as Record<ArtStyleKey, ArtStyleDisplay>;

/**
 * Get art style prompt by key (BE use only).
 */
export function getArtStylePrompt(key: ArtStyleKey): string {
  return ART_STYLES[key].stylePrompt;
}

/**
 * Get art style display data by key (FE-safe).
 */
export function getArtStyleDisplay(key: ArtStyleKey): ArtStyleDisplay {
  return ART_STYLE_DISPLAY[key];
}
